if (!customElements.get('sooler-product-grid-offers')) {
  customElements.define('sooler-product-grid-offers', class extends HTMLElement {
    connectedCallback() {
      window.clearTimeout(this.disconnectTimer);
      if (this.initialized) {
        this.startObserver();
        this.scheduleLayout();
        return;
      }

      this.initialized = true;
      this.refreshDomReferences();
      this.offerElements = Array.from(this.querySelectorAll('[data-sooler-grid-offer]'));
      this.viewerVideo = null;
      this.viewerMuted = true;
      this.playingInlineVideos = [];
      this.lastFocusedElement = null;
      this.previousBodyOverflow = '';
      this.layoutTimer = null;
      this.mobileHeightFrame = null;
      this.resizeTimer = null;
      this.onKeydown = this.handleKeydown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onExternalGridUpdate = this.handleExternalGridUpdate.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.observer = new MutationObserver(() => this.scheduleLayout());

      this.layoutOffers();
      this.initializeVideoCards();
      this.initializeViewer();
      this.startObserver();
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('sezerium:variant:initialize', this.onExternalGridUpdate);
      document.addEventListener('sezerium:js:unload', this.onExternalGridUpdate);
      document.addEventListener('pagination:page-changed', this.onExternalGridUpdate);
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.clearTimeout(this.disconnectTimer);
      this.disconnectTimer = window.setTimeout(() => {
        if (this.isConnected) {
          this.connectedCallback();
          return;
        }

        this.teardown();
      }, 0);
    }

    refreshDomReferences() {
      const grid = this.querySelector('[data-sooler-offer-grid]');
      const viewer = this.querySelector('[data-sooler-grid-viewer]');
      if (grid) this.grid = grid;
      if (viewer) {
        this.viewer = viewer;
        this.viewerMedia = viewer.querySelector('[data-sooler-grid-viewer-media]');
        this.viewerClose = viewer.querySelector('[data-sooler-grid-viewer-close]');
        this.viewerPlay = viewer.querySelector('[data-sooler-grid-viewer-play]');
        this.viewerSound = viewer.querySelector('[data-sooler-grid-viewer-sound]');
      }
    }

    startObserver() {
      if (!this.observer || !this.initialized || !this.isConnected) return;
      this.observer.disconnect();
      this.observer.observe(this, { childList: true, subtree: true });
    }

    scheduleLayout(delay = 120) {
      if (!this.initialized) return;
      window.clearTimeout(this.layoutTimer);
      this.layoutTimer = window.setTimeout(() => {
        if (!this.isConnected || !this.initialized) return;
        this.layoutOffers();
        this.initializeVideoCards();
      }, delay);
    }

    handleExternalGridUpdate() {
      this.scheduleLayout(180);
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.scheduleMobileOfferPairHeight(), 120);
    }

    scheduleMobileOfferPairHeight() {
      window.cancelAnimationFrame(this.mobileHeightFrame);
      this.mobileHeightFrame = window.requestAnimationFrame(() => this.syncMobileOfferPairHeight());
    }

    syncMobileOfferPairHeight() {
      if (!this.grid) return;

      const offers = Array.from(this.grid.querySelectorAll('[data-sooler-grid-offer]'));
      offers.forEach((offer) => offer.classList.remove('sooler-grid-offer-column--mobile-pair'));

      if (!window.matchMedia('(max-width: 767px)').matches) {
        this.style.removeProperty('--sooler-grid-offer-mobile-height');
        return;
      }

      const visibleItems = Array.from(this.grid.children).filter((item) => (
        !item.classList.contains('loading-overlay') && getComputedStyle(item).display !== 'none'
      ));
      const productHeights = visibleItems
        .filter((item) => !item.hasAttribute('data-sooler-grid-offer'))
        .map((item) => Math.round(item.getBoundingClientRect().height))
        .filter((height) => height > 0)
        .sort((first, second) => first - second);

      if (!productHeights.length) {
        this.style.removeProperty('--sooler-grid-offer-mobile-height');
        return;
      }

      const referenceHeight = productHeights[Math.floor(productHeights.length / 2)];
      this.style.setProperty('--sooler-grid-offer-mobile-height', `${referenceHeight}px`);

      visibleItems.forEach((item, index) => {
        if (!item.hasAttribute('data-sooler-grid-offer')) return;
        const peerIndex = index % 2 === 0 ? index + 1 : index - 1;
        const peer = visibleItems[peerIndex];
        if (peer?.hasAttribute('data-sooler-grid-offer')) {
          item.classList.add('sooler-grid-offer-column--mobile-pair');
        }
      });
    }

    teardown() {
      window.clearTimeout(this.layoutTimer);
      window.clearTimeout(this.resizeTimer);
      window.cancelAnimationFrame(this.mobileHeightFrame);
      this.observer?.disconnect();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('sezerium:variant:initialize', this.onExternalGridUpdate);
      document.removeEventListener('sezerium:js:unload', this.onExternalGridUpdate);
      document.removeEventListener('pagination:page-changed', this.onExternalGridUpdate);
      window.removeEventListener('resize', this.onResize);
      if (this.viewer && !this.viewer.hidden) this.closeViewer();
      else if (this.viewer && this.viewer.parentElement === document.body) this.appendChild(this.viewer);
      this.initialized = false;
    }

    getDesktopColumns() {
      const columns = Number.parseInt(this.dataset.columnsDesktop, 10);
      return Number.isInteger(columns) && columns > 0 ? columns : 4;
    }

    getGridPoint(position, columns) {
      const index = position - 1;
      return {
        row: Math.floor(index / columns),
        column: index % columns
      };
    }

    isBalancedPositionAllowed(candidate, placedPositions, columns, tier) {
      const point = this.getGridPoint(candidate, columns);

      return placedPositions.every((position) => {
        const placedPoint = this.getGridPoint(position, columns);
        const rowDistance = Math.abs(point.row - placedPoint.row);
        const columnDistance = Math.abs(point.column - placedPoint.column);

        if (tier === 0) {
          if (rowDistance === 0) return false;
          if (rowDistance === 1 && columnDistance === 0) return false;
        } else if (tier === 1) {
          if (rowDistance === 0 && columnDistance < 2) return false;
          if (rowDistance === 1 && columnDistance === 0) return false;
        } else if (tier === 2 && rowDistance === 0 && columnDistance < 2) {
          return false;
        }

        return true;
      });
    }

    getCandidateScore(candidate, placedPositions, totalItems, columns) {
      const anchors = [0, ...placedPositions, totalItems + 1].sort((a, b) => a - b);
      let left = 0;
      let right = totalItems + 1;

      for (let index = 0; index < anchors.length - 1; index += 1) {
        if (candidate > anchors[index] && candidate < anchors[index + 1]) {
          left = anchors[index];
          right = anchors[index + 1];
          break;
        }
      }

      const gapSize = right - left;
      const distanceFromGapCenter = Math.abs(candidate - ((left + right) / 2));
      const point = this.getGridPoint(candidate, columns);
      const nearestGridDistance = placedPositions.length
        ? Math.min(...placedPositions.map((position) => {
          const placedPoint = this.getGridPoint(position, columns);
          return Math.abs(point.row - placedPoint.row) + Math.abs(point.column - placedPoint.column);
        }))
        : columns;

      return (gapSize * 12) - (distanceFromGapCenter * 3) + (nearestGridDistance * 5) + (Math.random() * 4);
    }

    getPlacementScore(positions, totalItems, columns) {
      const sortedPositions = [...positions].sort((a, b) => a - b);
      const anchors = [0, ...sortedPositions, totalItems + 1];
      const gaps = anchors.slice(1).map((position, index) => position - anchors[index]);
      const idealGap = (totalItems + 1) / (sortedPositions.length + 1);
      const gapVariance = gaps.reduce((sum, gap) => sum + ((gap - idealGap) ** 2), 0);
      let minimumGridDistance = totalItems;

      for (let first = 0; first < sortedPositions.length; first += 1) {
        const firstPoint = this.getGridPoint(sortedPositions[first], columns);
        for (let second = first + 1; second < sortedPositions.length; second += 1) {
          const secondPoint = this.getGridPoint(sortedPositions[second], columns);
          const distance = Math.abs(firstPoint.row - secondPoint.row) + Math.abs(firstPoint.column - secondPoint.column);
          minimumGridDistance = Math.min(minimumGridDistance, distance);
        }
      }

      return (minimumGridDistance * 30) - (gapVariance * 4) + Math.random();
    }

    findBalancedPlacements(offers, availablePositions, fixedPositions, totalItems, columns) {
      const attemptsPerTier = 18;

      for (let tier = 0; tier <= 3; tier += 1) {
        const solutions = [];

        for (let attempt = 0; attempt < attemptsPerTier; attempt += 1) {
          const candidates = [...availablePositions];
          const placedPositions = [...fixedPositions];
          const assignments = new Map();
          const offerOrder = [...offers];

          if (attempt > 0) {
            for (let index = offerOrder.length - 1; index > 0; index -= 1) {
              const randomIndex = Math.floor(Math.random() * (index + 1));
              [offerOrder[index], offerOrder[randomIndex]] = [offerOrder[randomIndex], offerOrder[index]];
            }
          }

          let valid = true;
          offerOrder.forEach((offer) => {
            if (!valid) return;
            const scoredCandidates = candidates
              .filter((position) => this.isBalancedPositionAllowed(position, placedPositions, columns, tier))
              .map((position) => ({
                position,
                score: this.getCandidateScore(position, placedPositions, totalItems, columns)
              }))
              .sort((first, second) => second.score - first.score);

            if (!scoredCandidates.length) {
              valid = false;
              return;
            }

            const poolSize = attempt === 0 ? 1 : Math.min(3, scoredCandidates.length);
            const poolIndex = Math.floor((Math.random() ** 2) * poolSize);
            const selectedPosition = scoredCandidates[poolIndex].position;
            assignments.set(offer, selectedPosition);
            placedPositions.push(selectedPosition);
            candidates.splice(candidates.indexOf(selectedPosition), 1);
          });

          if (valid) {
            solutions.push({
              assignments,
              positions: placedPositions,
              score: this.getPlacementScore(placedPositions, totalItems, columns)
            });
          }
        }

        if (solutions.length) {
          solutions.sort((first, second) => second.score - first.score);
          const bestSolutions = solutions.slice(0, Math.min(4, solutions.length));
          const selectedSolution = bestSolutions[Math.floor(Math.random() * bestSolutions.length)];
          return { assignments: selectedSolution.assignments, tier };
        }
      }

      const assignments = new Map();
      offers.forEach((offer, index) => assignments.set(offer, availablePositions[index]));
      return { assignments, tier: 3 };
    }

    layoutOffers() {
      this.refreshDomReferences();
      if (!this.grid) return;

      this.observer?.disconnect();
      const gridItems = Array.from(this.grid.children).filter((item) => (
        item.classList.contains('columns') && !item.hasAttribute('data-sooler-grid-offer')
      ));
      const discoveredOffers = Array.from(this.querySelectorAll('[data-sooler-grid-offer]'));
      discoveredOffers.forEach((offer) => {
        const blockId = offer.dataset.soolerOfferBlockId;
        const existingIndex = this.offerElements.findIndex((item) => item.dataset.soolerOfferBlockId === blockId);
        if (existingIndex === -1) this.offerElements.push(offer);
        else if (this.offerElements[existingIndex] !== offer) this.offerElements[existingIndex] = offer;
      });
      const offers = this.offerElements.filter(Boolean);
      if (!offers.length) {
        this.startObserver();
        return;
      }

      offers.forEach((offer) => {
        offer.classList.remove('sezerium-remove', 'sezerium-original', 'sezerium-product', 'sezerium-variable');
        offer.remove();
      });
      const totalItems = gridItems.length + offers.length;
      const placements = new Map();
      const usedPositions = new Set();
      const fixedOffers = offers.filter((offer) => offer.dataset.soolerOfferPlacement === 'fixed');
      const automaticOffers = offers.filter((offer) => offer.dataset.soolerOfferPlacement !== 'fixed');
      const columns = this.getDesktopColumns();
      const placementSignature = [
        gridItems.length,
        columns,
        this.dataset.distribution,
        ...offers.map((offer) => `${offer.dataset.soolerOfferBlockId}:${offer.dataset.soolerOfferPlacement}:${offer.dataset.soolerOfferPosition}`)
      ].join('|');
      const shouldRecalculate = this.placementSignature !== placementSignature;

      const reserveNearestPosition = (requestedPosition) => {
        const requested = Math.max(1, Math.min(totalItems, requestedPosition));
        for (let offset = 0; offset < totalItems; offset += 1) {
          const forward = requested + offset;
          if (forward <= totalItems && !usedPositions.has(forward)) return forward;
          const backward = requested - offset;
          if (backward >= 1 && !usedPositions.has(backward)) return backward;
        }
        return 1;
      };

      fixedOffers.forEach((offer) => {
        const position = reserveNearestPosition(Number.parseInt(offer.dataset.soolerOfferPosition, 10) || 1);
        placements.set(position, offer);
        usedPositions.add(position);
        offer.dataset.soolerResolvedPosition = position;
      });

      if (this.dataset.distribution === 'even') {
        automaticOffers.forEach((offer, index) => {
          const requested = Math.round(((index + 1) * (totalItems + 1)) / (automaticOffers.length + 1));
          const position = reserveNearestPosition(requested);
          placements.set(position, offer);
          usedPositions.add(position);
          offer.dataset.soolerResolvedPosition = position;
        });
      } else {
        const hasCompleteCachedPlacement = !shouldRecalculate && automaticOffers.every((offer) => {
          const position = Number.parseInt(offer.dataset.soolerResolvedPosition, 10);
          return Number.isInteger(position) && position > 0;
        });

        if (hasCompleteCachedPlacement) {
          automaticOffers.forEach((offer) => {
            const position = reserveNearestPosition(Number.parseInt(offer.dataset.soolerResolvedPosition, 10));
            placements.set(position, offer);
            usedPositions.add(position);
            offer.dataset.soolerResolvedPosition = position;
          });
        } else {
          automaticOffers.forEach((offer) => delete offer.dataset.soolerResolvedPosition);
          const availablePositions = Array.from({ length: totalItems }, (_, index) => index + 1)
            .filter((position) => !usedPositions.has(position));
          const balancedPlacement = this.findBalancedPlacements(
            automaticOffers,
            availablePositions,
            Array.from(usedPositions),
            totalItems,
            columns
          );

          automaticOffers.forEach((offer) => {
            const position = balancedPlacement.assignments.get(offer);
            if (!position) return;
            placements.set(position, offer);
            usedPositions.add(position);
            offer.dataset.soolerResolvedPosition = position;
            offer.dataset.soolerPlacementTier = balancedPlacement.tier;
          });
        }
      }

      this.placementSignature = placementSignature;

      const fragment = document.createDocumentFragment();
      let gridItemIndex = 0;
      for (let position = 1; position <= totalItems; position += 1) {
        const offer = placements.get(position);
        if (offer) {
          offer.hidden = false;
          fragment.appendChild(offer);
        } else if (gridItems[gridItemIndex]) {
          fragment.appendChild(gridItems[gridItemIndex]);
          gridItemIndex += 1;
        }
      }

      const loadingOverlay = Array.from(this.grid.children).find((item) => item.classList.contains('loading-overlay'));
      this.grid.insertBefore(fragment, loadingOverlay || null);
      this.startObserver();
      this.scheduleMobileOfferPairHeight();
    }

    initializeVideoCards() {
      this.videoCards = Array.from(this.querySelectorAll('[data-sooler-grid-video-card]'));
      this.videoCards.forEach((card) => {
        if (card.dataset.soolerVideoReady === 'true') return;
        card.dataset.soolerVideoReady = 'true';
        const video = card.querySelector('video');
        const openButton = card.querySelector('[data-sooler-grid-video-open]');
        const soundButton = card.querySelector('[data-sooler-grid-video-sound]');
        if (!video) return;

        const open = (event) => {
          event?.stopPropagation();
          this.openViewer(card);
        };

        card.addEventListener('click', (event) => {
          if (event.target.closest('[data-sooler-grid-video-sound]')) return;
          open(event);
        });
        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open(event);
          }
        });
        openButton?.addEventListener('click', open);
        soundButton?.addEventListener('click', (event) => {
          event.stopPropagation();
          video.muted = !video.muted;
          soundButton.classList.toggle('is-unmuted', !video.muted);
          soundButton.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
        });

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) video.pause();
        else if (video.autoplay) video.play()?.catch(() => {});
      });
    }

    initializeViewer() {
      if (!this.viewer) return;
      this.viewerClose?.addEventListener('click', () => this.closeViewer());
      this.viewerPlay?.addEventListener('click', () => this.toggleViewerPlayback());
      this.viewerSound?.addEventListener('click', () => this.toggleViewerSound());
      this.viewer.addEventListener('click', (event) => {
        if (event.target === this.viewer) this.closeViewer();
      });
      this.viewerMedia?.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        this.toggleViewerPlayback();
      });
    }

    openViewer(card) {
      const sourceVideo = card.querySelector('video');
      if (!sourceVideo || !this.viewer || !this.viewerMedia) return;

      this.viewerVideo?.remove();
      this.viewerVideo = sourceVideo.cloneNode(true);
      this.viewerVideo.className = 'sooler-grid-offer-viewer__video';
      this.viewerVideo.removeAttribute('autoplay');
      this.viewerVideo.autoplay = false;
      this.viewerVideo.controls = false;
      this.viewerVideo.loop = true;
      this.viewerVideo.muted = true;
      this.viewerVideo.preload = 'metadata';
      this.viewerVideo.addEventListener('play', () => this.updateViewerControls());
      this.viewerVideo.addEventListener('pause', () => this.updateViewerControls());
      this.viewerMedia.appendChild(this.viewerVideo);

      this.playingInlineVideos = this.videoCards
        .map((videoCard) => videoCard.querySelector('video'))
        .filter((video) => video && !video.paused);
      this.videoCards.forEach((videoCard) => videoCard.querySelector('video')?.pause());

      this.lastFocusedElement = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.viewer.hidden = false;
      this.viewer.setAttribute('aria-hidden', 'false');
      document.body.appendChild(this.viewer);
      this.viewerVideo.currentTime = sourceVideo.currentTime || 0;
      this.viewerVideo.muted = this.viewerMuted;
      this.viewerVideo.play()?.catch(() => this.updateViewerControls());
      this.viewerClose?.focus();
      this.updateViewerControls();
    }

    closeViewer() {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewerVideo?.pause();
      this.viewer.hidden = true;
      this.viewer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.previousBodyOverflow;
      this.appendChild(this.viewer);
      this.playingInlineVideos.forEach((video) => video.play()?.catch(() => {}));
      this.lastFocusedElement?.focus?.();
    }

    toggleViewerPlayback() {
      if (!this.viewerVideo) return;
      if (this.viewerVideo.paused) this.viewerVideo.play()?.catch(() => {});
      else this.viewerVideo.pause();
      this.updateViewerControls();
    }

    toggleViewerSound() {
      if (!this.viewerVideo) return;
      this.viewerMuted = !this.viewerMuted;
      this.viewerVideo.muted = this.viewerMuted;
      this.updateViewerControls();
    }

    updateViewerControls() {
      if (!this.viewerVideo) return;
      this.viewerPlay?.classList.toggle('is-paused', this.viewerVideo.paused);
      this.viewerPlay?.setAttribute('aria-label', this.viewerVideo.paused ? 'Play video' : 'Pause video');
      this.viewerSound?.classList.toggle('is-unmuted', !this.viewerMuted);
      this.viewerSound?.setAttribute('aria-label', this.viewerMuted ? 'Turn sound on' : 'Turn sound off');
    }

    handleKeydown(event) {
      if (!this.viewer || this.viewer.hidden) return;
      if (event.key === 'Escape') this.closeViewer();
    }

    handleBlockSelect(event) {
      const offer = event.target.closest?.('[data-sooler-grid-offer]');
      if (!offer || !this.contains(offer)) return;
      offer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}
